import React, {useCallback} from 'react';
import styled from 'styled-components';
import Paper from '@material-ui/core/Paper/Paper';
import {Image} from '../../elements/images/Image';
import {Typography, Button, VerticalSpacer, Eyebrow} from '../../elements';
import {AnimateOnViewWrap} from '../../wraps/animation';
import {AccentColorOptions} from 'epic-ue-theme';
import {useLocalization} from '@epic-mw/localization';

const PersonWrapper = styled.div`
    .mui-paper {
        background: none;
        ${(p) => p.theme.breakpoints.up('xl')} {
            padding: ${(p) => p.theme.spacing(4)}px;
        }
    }
    * {
        text-align: center;
    }
    .paragraph {
        color: ${(p) => p.theme.palette.text.secondary};
    }
    .bio-content button.tertiary {
        min-height: 28px;
    }

    margin-bottom: 2px;
`;

const Avatar = styled.div`
    width: 220px;
    height: 220px;
    margin: 0 auto ${(p) => p.theme.spacing(3)}px auto;
    ${(p) => p.theme.breakpoints.down('sm')} {
        width: 120px;
        height: 120px;
    }
    img {
        border-radius: 9999px;
    }
`;
export type ClickIndexHandler = (index: number) => void;
export interface SocialProps {
    platform: string;
    url: string;
}

interface PersonalInfoProps {
    name?: string;
    caption?: string;
    shortInfo?: string;
    isInModal?: boolean;
}
export interface PersonProps {
    index?: number;
    animationDelay?: number;
    image?: string;
    name?: string;
    caption?: string;
    shortInfo?: string;
    bio?: string;
    socials?: SocialProps[];
    accentColor?: AccentColorOptions;
    id?: string;
    onClick?: ClickIndexHandler;
}

export const PersonalInfo = ({
    name,
    caption,
    shortInfo,
    isInModal = false
}: PersonalInfoProps): JSX.Element => {
    return (
        <div className="personal-info">
            {name && (
                <>
                    <Typography variant="h3" big={true}>
                        {name}
                    </Typography>
                    <VerticalSpacer variant="paragraph" />
                </>
            )}
            {caption && <Eyebrow text={caption} />}
            {shortInfo && (
                <>
                    <VerticalSpacer variant="paragraph" />
                    <Typography variant="p" caption={!isInModal}>
                        {shortInfo}
                    </Typography>
                </>
            )}
        </div>
    );
};

export const Person = ({
    index,
    image,
    name,
    caption,
    shortInfo,
    bio,
    accentColor,
    onClick,
    id
}: PersonProps): JSX.Element => {
    const getMessage = useLocalization();
    const bioLink = useCallback(
        (e) => {
            e.preventDefault();
            if (onClick && typeof onClick === 'function') {
                if (index !== null && typeof index === 'number') {
                    onClick(index);
                }
            }
        },
        [onClick, index]
    );

    let animationDelay = 1;
    if (index && index !== 0) {
        animationDelay = index / 10;
    } else if (index === 0) {
        animationDelay = 1 / 10;
    }

    return (
        <PersonWrapper className="person">
            <AnimateOnViewWrap translateY={65} delay={animationDelay}>
                <Paper elevation={0} square={true} className="mui-paper">
                    {image && (
                        <Avatar>
                            <Image src={image} ratio="square" fit="cover" />
                        </Avatar>
                    )}

                    <div className="bio-content">
                        <PersonalInfo name={name} caption={caption} shortInfo={shortInfo} />
                        {bio && (
                            <>
                                <VerticalSpacer variant="custom" spacing={3} downSmSpacing={2} />
                                <Button
                                    color={accentColor}
                                    type="tertiary"
                                    onClick={bioLink}
                                    href=""
                                    id={id}>
                                    {getMessage('epic.ui.people.bio.label') || 'bio'}
                                </Button>
                            </>
                        )}
                    </div>
                </Paper>
            </AnimateOnViewWrap>
        </PersonWrapper>
    );
};
